import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import ExportPreviewModal from '../components/ExportPreviewModal';
import { hasNavPermission } from '../components/ProtectedRoute';
import { isoYearOfDate } from '../components/HistoryFilters';
import { useAuth } from '../context/AuthContext';
import { isoWeekYear } from '../utils/dates';
import { addSums, finishSums, fmtMoney, sumCloses, sumSales } from '../utils/salesSummary';
import './CompaniesPage.css';
import './SalesFormPage.css';
import '../styles/topbarButtons.css';
import '../styles/pageHeader.css';
import '../styles/productionSummary.css';

// Resumen de Ventas (Ventas): the Historial de Ventas summary figures (Total cajas, Total cajas 43lbs,
// Precio real promedio, Precio 43lbs promedio, Total real — Total 43lbs is always the same amount)
// week by week for a year, plus the year's total (utils/salesSummary.js). Every Monday morning the server
// closes the week before (manage.py close_sales_weeks → core.WeeklySalesClose): a closed week 🔒 shows
// its frozen figures, with ⚠️ when the sales have changed since; "Reajustar" (users who can see
// prices) then recalculates the week from the current sales and saves it — only by hand. Open weeks, and any Hacienda /
// Exportadora filter (closes are per company), are worked out live from the sales. Money columns only
// for users who can see sale prices. "Exportar" previews the table and downloads it as Excel, PDF or
// JPEG, with an Estado column (Cerrada / Abierta).
// Data: sales/ (core/views.py SaleViewSet), sales-closes/?year= and sales-closes/readjust/
// (WeeklySalesCloseViewSet).

// ---- export ----
const GREEN = '2F7D32';
const LIGHT_GREEN = 'C8E6C9';
const fmtInt = (n) => Math.round(n).toLocaleString('es-EC');
const weekState = (w) => {
  if (!w.closed) return 'Abierta';
  return w.changed ? 'Cerrada · cambió' : 'Cerrada';
};

// Header and rows (numbers kept raw; `text` formats a row for the PDF / preview). Money columns only
// with price permission.
function sheetModel(weeks, total, year, withPrices) {
  const head = ['SEMANA', 'TOTAL CAJAS', 'TOTAL CAJAS 43LBS',
    ...(withPrices ? ['PRECIO REAL PROM.', 'PRECIO 43LBS PROM.', 'TOTAL REAL'] : []), 'ESTADO'];
  const cells = (label, t, state) => [label, t.totalBoxes, Math.round(t.totalBoxes43),
    ...(withPrices ? [t.avgReal, t.avg43, t.totalReal] : []), state];
  const body = weeks.map((w) => cells(`Semana ${w.week}`, w, weekState(w)));
  const totalRow = cells(`Total ${year}`, total, '');
  const moneyCols = withPrices ? [3, 4, 5] : [];
  const text = (row) => row.map((v, i) => {
    if (i === 0 || i === row.length - 1) return v;
    return moneyCols.includes(i) ? fmtMoney(v) : fmtInt(v);
  });
  return { head, body, totalRow, moneyCols, text };
}

async function exportExcel(title, subtitle, fileName, model) {
  const { head, body, totalRow, moneyCols } = model;
  const width = head.length;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Resumen de Ventas', { views: [{ state: 'frozen', ySplit: 4 }] });
  const thin = { style: 'thin', color: { argb: 'FF000000' } };
  const border = { top: thin, left: thin, bottom: thin, right: thin };
  const solid = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } });

  sheet.mergeCells(1, 1, 1, width);
  const t = sheet.getCell(1, 1);
  t.value = title;
  t.fill = solid(GREEN);
  t.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
  t.alignment = { horizontal: 'center' };
  sheet.mergeCells(2, 1, 2, width);
  sheet.getCell(2, 1).value = subtitle;
  sheet.getCell(2, 1).font = { bold: true };
  sheet.getCell(2, 1).alignment = { horizontal: 'center' };
  sheet.addRow([]);
  const headRow = sheet.addRow(head);
  headRow.eachCell((c) => {
    c.fill = solid(LIGHT_GREEN);
    c.border = border;
    c.font = { bold: true, size: 10 };
    c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  });
  [...body, totalRow].forEach((cells, r) => {
    const isTotal = r === body.length;
    const row = sheet.addRow(cells);
    row.eachCell({ includeEmpty: true }, (c, col) => {
      if (col > width) return;
      const i = col - 1;
      c.border = border;
      c.font = { size: 10, bold: isTotal };
      if (isTotal) c.fill = solid('EEF4EC');
      if (i === 0 || i === width - 1) {
        c.alignment = { horizontal: i === 0 ? 'left' : 'center' };
      } else {
        c.numFmt = moneyCols.includes(i) ? '"$"#,##0.00' : '#,##0';
        c.alignment = { horizontal: 'right' };
      }
    });
  });
  head.forEach((h, i) => { sheet.getColumn(i + 1).width = i === 0 ? 14 : 18; });
  sheet.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function exportPDF(title, subtitle, fileName, model) {
  const { head, body, totalRow, text } = model;
  const width = head.length;
  const doc = new jsPDF({ orientation: 'landscape', format: 'a4' });
  autoTable(doc, {
    startY: 12,
    theme: 'grid',
    head: [
      [{ content: title, colSpan: width, styles: { fillColor: `#${GREEN}`, textColor: '#ffffff', halign: 'center', fontSize: 12 } }],
      [{ content: subtitle, colSpan: width, styles: { fillColor: '#ffffff', halign: 'center', fontStyle: 'bold' } }],
      head,
    ],
    body: body.map(text),
    foot: [text(totalRow)],
    showFoot: 'lastPage',
    styles: { fontSize: 9, cellPadding: 1.5, halign: 'right', valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0] },
    headStyles: { fillColor: `#${LIGHT_GREEN}`, textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
    footStyles: { fillColor: '#eef4ec', textColor: [0, 0, 0], fontStyle: 'bold', halign: 'right' },
    columnStyles: { 0: { halign: 'left' }, [width - 1]: { halign: 'center' } },
  });
  doc.save(`${fileName}.pdf`);
}

const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));

export default function SalesSummaryPage() {
  const { user } = useAuth();
  const canSeePrices = hasNavPermission(user, 'sale_prices');
  const [sales, setSales] = useState([]);
  const [closes, setCloses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [year, setYear] = useState(String(isoWeekYear(new Date())));
  const [company, setCompany] = useState('');
  const [farm, setFarm] = useState('');
  const [exporter, setExporter] = useState('');
  const [preview, setPreview] = useState(false);
  const [readjusting, setReadjusting] = useState(null);  // week being readjusted
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api.get('sales/')
      .then((r) => setSales(r.data))
      .catch((err) => setStatus(apiErrorText(err, 'No se pudieron cargar las ventas.')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setCloses([]);
    api.get('sales-closes/', { params: { year } })
      .then((r) => setCloses(r.data))
      .catch(() => setCloses([]));  // without closes every week is shown live
  }, [year]);

  const options = useMemo(() => ({
    years: uniqueSorted([String(isoWeekYear(new Date())), ...sales.map((s) => isoYearOfDate(s.date))]).reverse(),
    companies: uniqueSorted(sales.map((s) => s.company_name)),
    farms: uniqueSorted(sales.filter((s) => !company || s.company_name === company).map((s) => s.farm_name)),
    exporters: uniqueSorted(sales.map((s) => s.exporter_name)),
  }), [sales, company]);

  const filtered = useMemo(() => sales.filter((s) => isoYearOfDate(s.date) === year
    && (!company || s.company_name === company)
    && (!farm || s.farm_name === farm)
    && (!exporter || s.exporter_name === exporter)), [sales, year, company, farm, exporter]);

  // One row per week that has sales (or a close), oldest first; then the year's total. A week counts as
  // closed when every company in it (within the Empresa filter) has been closed.
  const { weeks, total } = useMemo(() => {
    const useCloses = !farm && !exporter;
    const byWeek = new Map();
    const entry = (week) => {
      if (!byWeek.has(week)) byWeek.set(week, { sales: [], closes: [] });
      return byWeek.get(week);
    };
    filtered.forEach((s) => entry(s.week).sales.push(s));
    if (useCloses) {
      closes.filter((c) => !company || c.company_name === company).forEach((c) => entry(c.week).closes.push(c));
    }
    let yearSums = null;
    const rows = [...byWeek.entries()].sort((a, b) => a[0] - b[0]).map(([week, w]) => {
      const live = sumSales(w.sales);
      const closedCompanies = new Set(w.closes.map((c) => c.company_name));
      const closed = w.closes.length > 0 && w.sales.every((s) => closedCompanies.has(s.company_name));
      const sums = closed ? sumCloses(w.closes) : live;
      yearSums = yearSums ? addSums(yearSums, sums) : sums;
      const changed = closed && (live.count !== sums.count || live.totalBoxes !== sums.totalBoxes
        || Math.abs(live.totalReal - sums.totalReal) >= 0.01);
      const closedAt = closed ? w.closes.map((c) => c.closed_at).sort().pop() : null;
      const readjusted = closed ? w.closes.filter((c) => c.readjusted_at)
        .sort((a, b) => a.readjusted_at.localeCompare(b.readjusted_at)).pop() : null;
      return {
        week, closed, changed, closedAt, readjusted, closeCompanies: w.closes.map((c) => c.company),
        live: finishSums(live), ...finishSums(sums),
      };
    });
    return { weeks: rows, total: yearSums && finishSums(yearSums) };
  }, [filtered, closes, company, farm, exporter]);

  const fmtDate = (iso) => new Date(iso).toLocaleDateString('es-EC', { day: 'numeric', month: 'short', year: 'numeric' });
  const closeNote = (w) => {
    if (!w.closed) return 'Semana abierta: cifras en vivo de las ventas.';
    let note = `Semana cerrada el ${fmtDate(w.closedAt)}: cifras congeladas.`;
    if (w.readjusted) {
      note += `\nReajustada el ${fmtDate(w.readjusted.readjusted_at)}`
        + `${w.readjusted.readjusted_by_username ? ` por ${w.readjusted.readjusted_by_username}` : ''}.`;
    }
    if (!w.changed) return note;
    const live = [`${w.live.totalBoxes.toLocaleString('es-EC')} cajas`];
    if (canSeePrices) live.push(`Total real ${fmtMoney(w.live.totalReal)}`);
    return `${note}
Las ventas cambiaron después del cierre. Ahora: ${live.join(', ')}.`;
  };

  // "Reajustar": saves the week's current figures in place of the closed ones.
  const readjust = (w) => {
    if (!window.confirm(`¿Reajustar la semana ${w.week}? Se guardarán las cifras actuales de las ventas en lugar de las del cierre.`)) return;
    setReadjusting(w.week);
    setNotice(null);
    api.post('sales-closes/readjust/', { year: Number(year), week: w.week, companies: w.closeCompanies })
      .then((res) => {
        const byId = new Map(res.data.map((c) => [c.id, c]));
        setCloses((list) => list.map((c) => byId.get(c.id) || c));
        setNotice({ ok: true, text: `Semana ${w.week} reajustada.` });
      })
      .catch((err) => setNotice({ ok: false, text: apiErrorText(err, 'No se pudo reajustar la semana.') }))
      .finally(() => setReadjusting(null));
  };

  const exportTitle = `RESUMEN DE VENTAS ${year}`;
  const exportSubtitle = [company || 'Todas las Empresas', farm || 'Todas las Haciendas', exporter || 'Todas las Exportadoras'].join(' · ');
  const exportFile = ['Resumen_de_Ventas', year, company, farm, exporter].filter(Boolean).join('_').replace(/\s+/g, '_');
  const exportModel = weeks.length ? sheetModel(weeks, total, year, canSeePrices) : null;

  const select = (label, all, value, setValue, list) => (
    <select className="history-select" value={value} onChange={(e) => setValue(e.target.value)} aria-label={label}>
      {all && <option value="">{all}</option>}
      {list.map((v) => <option key={v} value={v}>{v}</option>)}
    </select>
  );

  const row = (key, label, t, className) => (
    <tr key={key} className={className}>
      <td>{label}</td>
      <td className="num">{t.totalBoxes.toLocaleString('es-EC')}</td>
      <td className="num">{Math.round(t.totalBoxes43).toLocaleString('es-EC')}</td>
      {canSeePrices && <td className="num">{fmtMoney(t.avgReal)}</td>}
      {canSeePrices && <td className="num">{fmtMoney(t.avg43)}</td>}
      {canSeePrices && <td className="num">{fmtMoney(t.totalReal)}</td>}
    </tr>
  );

  return (
    <div className="companies-root">
      <div className="page-topbar">
        <Link to="/ventas" className="topbar-btn">← Ventas</Link>
        <Link to="/ventas/historial" className="topbar-btn">📋 Historial de Ventas</Link>
        <button type="button" className="topbar-btn" onClick={() => setPreview(true)} disabled={loading || !weeks.length}>📤 Exportar</button>
      </div>
      <h1 className="companies-title">📊 Resumen de Ventas</h1>
      <p className="page-subtitle page-header-divided">
        Los totales del Historial de Ventas, semana por semana. Los montos solo cuentan ventas con Precio 43 lbs.
        Cada lunes se cierra la semana anterior 🔒 y sus cifras quedan guardadas.
      </p>

      <div className="history-filters">
        {select('Año', null, year, (v) => setYear(v), options.years)}
        {options.companies.length > 1 && select('Empresa', 'Todas las Empresas', company, (v) => { setCompany(v); setFarm(''); }, options.companies)}
        {select('Hacienda', 'Todas las Haciendas', farm, setFarm, options.farms)}
        {select('Exportadora', 'Todas las Exportadoras', exporter, setExporter, options.exporters)}
      </div>

      {status && <div className="companies-status companies-status-error">{status}</div>}
      {notice && (
        <div className={`companies-status ${notice.ok ? 'companies-status-success' : 'companies-status-error'}`}>{notice.text}</div>
      )}

      <div className="companies-card">
        {loading ? (
          <div className="companies-empty">Cargando…</div>
        ) : weeks.length === 0 ? (
          <div className="companies-empty">No hay ventas en {year} para los filtros seleccionados.</div>
        ) : (
          <table className="companies-table sales-summary-table">
            <thead>
              <tr>
                <th>Semana</th>
                <th className="num">Total cajas</th>
                <th className="num">Total cajas 43lbs</th>
                {canSeePrices && <th className="num">Precio real prom.</th>}
                {canSeePrices && <th className="num">Precio 43lbs prom.</th>}
                {canSeePrices && <th className="num">Total real</th>}
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => row(w.week, (
                <span className="sales-summary-week" title={closeNote(w)}>
                  Semana {w.week}
                  {w.closed && <span className="sales-summary-lock" aria-label="Semana cerrada">🔒</span>}
                  {w.changed && <span className="sales-summary-changed" aria-label="Cambió después del cierre">⚠️</span>}
                  {w.changed && canSeePrices && (
                    <button
                      type="button"
                      className="sales-summary-readjust"
                      onClick={() => readjust(w)}
                      disabled={readjusting !== null}
                      title="Guardar las cifras actuales de las ventas para esta semana"
                    >
                      {readjusting === w.week ? 'Reajustando…' : '↻ Reajustar'}
                    </button>
                  )}
                </span>
              ), w, w.changed ? 'sales-summary-row-changed' : undefined))}
              {row('total', `Total ${year}`, total, 'sales-summary-total')}
            </tbody>
          </table>
        )}
      </div>

      {preview && exportModel && (
        <ExportPreviewModal
          title={`${exportTitle} · ${exportSubtitle}`}
          fileName={exportFile}
          landscape
          onClose={() => setPreview(false)}
          onDownload={(format) => (format === 'excel'
            ? exportExcel(exportTitle, exportSubtitle, exportFile, exportModel)
            : exportPDF(exportTitle, exportSubtitle, exportFile, exportModel))}
        >
          <table className="ps-table sales-summary-print">
            <thead>
              <tr><th className="ps-title" colSpan={exportModel.head.length}>{exportTitle}</th></tr>
              <tr><th className="sales-summary-print-subtitle" colSpan={exportModel.head.length}>{exportSubtitle}</th></tr>
              <tr>{exportModel.head.map((h) => <th key={h} className="ps-farm-head">{h}</th>)}</tr>
            </thead>
            <tbody>
              {[...exportModel.body, exportModel.totalRow].map((cells, r) => (
                <tr key={cells[0]} className={r === exportModel.body.length ? 'sales-summary-print-total' : undefined}>
                  {exportModel.text(cells).map((v, i) => {
                    let cls = 'ps-num-right';
                    if (i === 0) cls = 'sales-summary-print-label';
                    else if (i === cells.length - 1) cls = 'sales-summary-print-state';
                    // eslint-disable-next-line react/no-array-index-key
                    return <td key={i} className={cls}>{v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </ExportPreviewModal>
      )}
    </div>
  );
}
